import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Order } from '@api/Orders/Order';
import { DataGrid, esES, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { currencyFormatter } from '@utils/Numbers';

interface SalesByBranchOfficesProps {
    orders: Order[];
}
interface SalesByBranchOffice {
    id: string;
    count: number;
    total: number;
    cashAmount: number;
    creditCardAmount: number;
    bankTransferAmount: number;
}

const SalesByBranchOffices: React.FunctionComponent<SalesByBranchOfficesProps> = (props) => {
    const { orders } = props;
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const [rows, setRows] = useState<SalesByBranchOffice[]>([]);

    const getBranchOfficeName = (branchOfficeId: string) => {
        const branchOffice = branchOffices.find((b) => b.id === branchOfficeId);
        return branchOffice?.name || 'Sin sucursal';
    };

    useEffect(() => {
        const array: SalesByBranchOffice[] = [];
        orders.forEach((order) => {
            const branchId = order.branchOfficeId || 'sin-sucursal';
            const index = array.findIndex((s) => s.id === branchId);
            if (index === -1) {
                array.push({
                    id: branchId,
                    count: 1,
                    total: order.total,
                    cashAmount: getOrderPaidBy(order, PaymentMethod.Cash),
                    creditCardAmount: getOrderPaidBy(order, PaymentMethod.CreditCard),
                    bankTransferAmount: getOrderPaidBy(order, PaymentMethod.BankTransfer)
                });
            } else {
                array[index].count += 1;
                array[index].total += order.total;
                array[index].cashAmount += getOrderPaidBy(order, PaymentMethod.Cash);
                array[index].creditCardAmount += getOrderPaidBy(order, PaymentMethod.CreditCard);
                array[index].bankTransferAmount += getOrderPaidBy(order, PaymentMethod.BankTransfer);
            }
        });
        setRows(array);
    }, [orders]);

    const columns = useMemo<GridColDef<SalesByBranchOffice>[]>(
        () => [
            {
                field: 'name',
                flex: 1.5,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Sucursal
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{getBranchOfficeName(row.id)}</Typography>,
                valueGetter: ({ row }) => getBranchOfficeName(row.id)
            },
            {
                field: 'count',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Pedidos
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.count}</Typography>,
                valueGetter: ({ row }) => row.count
            },
            {
                field: 'total',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.total)}</Typography>,
                valueGetter: ({ row }) => row.total
            },
            {
                field: 'cashAmount',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Efectivo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.cashAmount)}</Typography>,
                valueGetter: ({ row }) => row.cashAmount
            },
            {
                field: 'creditCardAmount',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Tarjeta
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.creditCardAmount)}</Typography>,
                valueGetter: ({ row }) => row.creditCardAmount
            },
            {
                field: 'bankTransferAmount',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Transferencia
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{currencyFormatter(row.bankTransferAmount)}</Typography>,
                valueGetter: ({ row }) => row.bankTransferAmount
            }
        ],
        [branchOffices]
    );

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                sx={{ borderWidth: 0 }}
                rows={rows}
                columns={columns}
                slots={{
                    toolbar: GridToolbar
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 }
                    }
                }}
            />
        </Box>
    );
};

export default SalesByBranchOffices;
